import { Injectable } from "@angular/core";
import { ExtendedJob, Job, JobMaterial, Material, Sample, SampleGroup, Entity, Animal } from "@common/types";
import { ISelectable } from "@common/types/selectable.interface";
import { createLookup } from "@common/util/create-lookup";
import { JobPharmaDataAccessService, SampleGroupMetadata } from "./job-pharma-data-access.service";
import { JobPharmaDetailService } from "./job-pharma-detail.service";
import { SampleService } from "src/app/samples";
import { CopyBufferService } from "@common/services";
import { MaterialType } from "../models/material-type";
import { AnimalService } from "src/app/animals/services/animal.service";
import { TableSortConfig } from "@common/models";

export type SampleGroupExtended = Entity<SampleGroup> & SampleGroupMetadata & ISelectable & {
    numSources?: number;
    taskFirst?: boolean;
    expanded?: boolean;
    classes?: {
        [key: string]: { [key: string]: boolean };
    };
}

@Injectable({
    providedIn: "root"
})
export class JobPharmaCoreService {
    // TODO: should be moved to service specific to JobPharmaSampleIndividual component
    private jobSampleCounts: Map<Job, number> = new Map();
    private jobAnimalCounts: Map<number, number> = new Map();
    private pendingSampleJobMaterialDeletions: Map<Job, Map<number, JobMaterial>> = new Map();
    private pendingAnimalJobMaterialDeletions: Map<number, Map<number, JobMaterial>> = new Map();

    constructor(
        private jobPharmaDataAccessService: JobPharmaDataAccessService,
        private jobPharmaDetailService: JobPharmaDetailService,
        private sampleService: SampleService,
        private copyBufferService: CopyBufferService,
        private animalService: AnimalService
    ){ }

    async getSampleJobMaterials(jobKey: number, pageNumber: number): Promise<JobMaterial[]> {
        return this.jobPharmaDataAccessService.getSampleJobMaterials(jobKey, pageNumber);
    }

    async getSampleCount(job: Job): Promise<number> {
        const count = await this.jobPharmaDataAccessService.getSampleCount(job.C_Job_key);
        this.calculateSampleCountMinusDeletions(job, count)
        return count;
    }

    async getSampleCountMinusPendingDeletions(job: Job): Promise<number> {
        if (this.jobSampleCounts.has(job)) {
            return this.jobSampleCounts.get(job);
        }

        const count = await this.jobPharmaDataAccessService.getSampleCount(job.C_Job_key);
        return this.calculateSampleCountMinusDeletions(job, count);
    }

    private calculateSampleCountMinusDeletions(job: Job, count: number) {
        const deletions = this.getIgnoredSampleJobMaterialKeysOnJob(job);
        const trueCount = count - deletions.size;
        this.jobSampleCounts.set(job, trueCount);
        return trueCount;
    }

    async getAnimalJobMaterials(jobKey: number, pageNumber: number, orderBy?: string): Promise<JobMaterial[]> {
        return this.jobPharmaDataAccessService.getAnimalJobMaterials(jobKey, pageNumber, orderBy, ['JobMaterialPastNicknames']);
    }

    async getAnimalCount(job: Job): Promise<number> {
        const count = await this.jobPharmaDataAccessService.getAnimalCount(job.C_Job_key);
        this.calculateAnimalCountMinusDeletions(job, count);
        return count
    }

    async getAnimalCountMinusPendingDeletions(job: Job): Promise<number> {
        if (this.jobAnimalCounts.has(job.C_Job_key)) {
            return this.jobAnimalCounts.get(job.C_Job_key);
        }

        const count = await this.jobPharmaDataAccessService.getAnimalCount(job.C_Job_key);
        return this.calculateAnimalCountMinusDeletions(job, count);
    }

    private calculateAnimalCountMinusDeletions(job: Job, count: number) {
        const deletions = this.getIgnoredAnimalJobMaterialKeysOnJob(job);
        const trueCount = count - deletions.size;
        this.jobAnimalCounts.set(job.C_Job_key, trueCount);
        return trueCount;
    }

    // TODO: The call to the jobPharmaDetailService will be replaced with a GraphQL query
    addSamplesToJob(samples: Sample[], job: Entity<Job & ExtendedJob> | Entity<Job>): Promise<any> {
        const materials = samples.map(s => s.Material) as Entity<Material>[];
        return this.jobPharmaDetailService.addMaterialsToJobIfMissing(job, materials);
    }

    onDropSamples(job: Entity<Job & ExtendedJob> | Entity<Job>): Promise<any> {
        const samples = this.sampleService.draggedSamples;
        this.sampleService.draggedSamples = [];

        return this.addSamplesToJob(samples, job);
    }

    onPasteSamples(job: Entity<Job & ExtendedJob> | Entity<Job>): Promise<any> {
        if (!this.copyBufferService.hasSamples()) {
            return Promise.resolve();
        }

        const samples = this.copyBufferService.paste();
        return this.addSamplesToJob(samples, job);
    }

    // TODO: again, call to the jobPharmaDetailSerivce will be replaced with GraphGL query
    async removeSampleJobMaterial(jobMaterial: JobMaterial): Promise<void> {
        const tasks = jobMaterial.Material.TaskMaterial.map(item => item.TaskInstance);
        const lockedTasks = this.jobPharmaDetailService.getLockedTasks(tasks);
        if (this.jobPharmaDetailService.hasLockedTasks(lockedTasks)) {
            this.jobPharmaDetailService.notifyMaterialsCannotBeRemovedFromLockedTask();

            return;
        }

        const job = jobMaterial.Job;
        const success = await this.jobPharmaDetailService.removeJobMaterial(jobMaterial);
        if (!success) {
            return;
        }

        const deletions = this.pendingSampleJobMaterialDeletions.get(job);
        if (!deletions) {
            this.pendingSampleJobMaterialDeletions.set(job, new Map().set(jobMaterial.C_JobMaterial_key, jobMaterial));
        } else {
            deletions.set(jobMaterial.C_JobMaterial_key, jobMaterial);
        }

        const count = this.jobSampleCounts.get(job);
        this.jobSampleCounts.set(job, count - 1);
    }

    rejectSampleJobMaterialChange(jobMaterial: JobMaterial) {
        const job = jobMaterial.Job;

        const deletions = this.pendingSampleJobMaterialDeletions.get(job);
        if (!deletions || !deletions.has(jobMaterial.C_JobMaterial_key)) {
            return;
        }

        deletions.delete(jobMaterial.C_JobMaterial_key);

        const count = this.jobSampleCounts.get(job);
        this.jobSampleCounts.set(job, count + 1);
    }

    removeAnimalJobMaterial(jobMaterial: JobMaterial) {
        const jobKey = jobMaterial.C_Job_key;

        const deletions = this.pendingAnimalJobMaterialDeletions.get(jobKey);
        if (!deletions) {
            this.pendingAnimalJobMaterialDeletions.set(jobKey, new Map().set(jobMaterial.C_JobMaterial_key, jobMaterial));
        } else {
            deletions.set(jobMaterial.C_JobMaterial_key, jobMaterial);
        }

        const count = this.jobAnimalCounts.get(jobKey);
        this.jobAnimalCounts.set(jobKey, count - 1);
    }

    getRemovedAnimalJobMaterials(jobKey: number) {
        const deletions = this.pendingAnimalJobMaterialDeletions.get(jobKey);

        return deletions ?? new Map<number, JobMaterial>();
    }

    rejectAnimalJobMaterialChange(jobMaterial: JobMaterial) {
        const job = jobMaterial.Job;

        const deletions = this.pendingAnimalJobMaterialDeletions.get(job.C_Job_key);
        if (!deletions || !deletions.has(jobMaterial.C_JobMaterial_key)) {
            return;
        }

        deletions.delete(jobMaterial.C_JobMaterial_key);

        const count = this.jobAnimalCounts.get(job.C_Job_key);
        this.jobAnimalCounts.set(job.C_Job_key, count + 1);
    }

    getAllSampleJobMaterialsOnJob(job: Job) {
        return this.jobPharmaDataAccessService.getAllMaterialsOnJob(job.C_Job_key, MaterialType.SAMPLE);
    }

    getAllAnimalJobMaterialsOnJob(job: Job) {
        return this.jobPharmaDataAccessService.getAllMaterialsOnJob(job.C_Job_key, MaterialType.ANIMAL);
    }

    getIgnoredSampleJobMaterialKeysOnJob(job: Job, includeDetached?: boolean): Set<number> {
        const map = this.pendingSampleJobMaterialDeletions.get(job);
        if (!map) {
            return new Set();
        }

        const jobMaterials = (<Entity<JobMaterial>[]>Array.from(map.values()))
            .filter((jm) => jm.entityAspect?.entityState?.isDeleted() ||  (includeDetached && jm.entityAspect?.entityState?.isDetached()));

        return new Set(jobMaterials.map(jm => jm.C_JobMaterial_key));
    }

    getIgnoredAnimalJobMaterialKeysOnJob(job: Job, includeDetached?: boolean): Set<number> {
        const map = this.pendingAnimalJobMaterialDeletions.get(job.C_Job_key);
        if(!map) {
            return new Set();
        }

        const jobMaterials = (<Entity<JobMaterial>[]>Array.from(map.values()))
            .filter((jm) => jm.entityAspect?.entityState?.isDeleted() || (includeDetached && jm.entityAspect?.entityState?.isDetached()));

        return new Set(jobMaterials.map(jm => jm.C_JobMaterial_key));
    }

    clearIgnoredSamplesOnAllJobs() {
        this.pendingSampleJobMaterialDeletions.clear();
    }

    clearIgnoredAnimalsOnAllJobs() {
        this.pendingAnimalJobMaterialDeletions.clear();
    }

    cleanupIgnoredSamplesOnJob(job: Job) {
        const map = this.pendingSampleJobMaterialDeletions.get(job);
        if (!map) {
            return;
        }

        const jobMaterials = Array.from(map.values()) as Entity<JobMaterial>[];
        jobMaterials.forEach(jm => jm.entityAspect?.entityState?.isDetached() && map.delete(jm.C_Material_key));
    }

    cleanupIgnoredAnimalsOnJob(job: Job) {
        const map = this.pendingAnimalJobMaterialDeletions.get(job.C_Job_key);
        if (!map) {
            return;
        }

        const jobMaterials = Array.from(map.values()) as Entity<JobMaterial>[];
        jobMaterials.forEach(jm => jm.entityAspect?.entityState?.isDetached() && map.delete(jm.C_Material_key));
    }

    async populateSampleGroupsMetadata(sampleGroups: SampleGroupExtended[]): Promise<void> {
        if (!sampleGroups || sampleGroups.length === 0) {
            return;
        }

        const sampleGroupsLookup = createLookup(sampleGroups, "C_SampleGroup_key");
        const sampleGroupsMetadata = await this.jobPharmaDataAccessService.getSampleGroupsMetadata(sampleGroups.map(sg => sg.C_SampleGroup_key));
        for (const metadata of sampleGroupsMetadata) {
            const key = metadata.C_SampleGroup_key;
            const sg = sampleGroupsLookup.get(key);
            sg.SamplesCreatedCount = metadata.SamplesCreatedCount;
            sg.NumberOfAnimalSourcesCount = metadata.NumberOfAnimalSourcesCount;
            sg.NumberOfSampleSourcesCount = metadata.NumberOfSampleSourcesCount;
            sg.NumberOfAnimalPlaceholderSourcesCount = metadata.NumberOfAnimalPlaceholderSourcesCount;
            sg.HasEndStateMemberTask = metadata.HasEndStateMemberTask;
            sg.numSources = (
                sg.NumberOfAnimalSourcesCount +
                sg.NumberOfSampleSourcesCount +
                sg.NumberOfAnimalPlaceholderSourcesCount
            );
        }
    }

    addMaterialsToJob(materials: (Animal | Sample)[], job: Entity<Job & ExtendedJob> | Entity<Job>): Promise<any> {
        const materialsToAdd = materials.map(m => m.Material) as Entity<Material>[];
        return this.jobPharmaDetailService.addMaterialsToJobIfMissing(job, materialsToAdd);
    }


    onDropAnimals(job: Entity<Job & ExtendedJob> | Entity<Job>): Promise<any> {
        const animals = this.animalService.draggedAnimals;
        this.animalService.draggedAnimals = [];

        return this.addMaterialsToJob(animals, job);
    }

    async checkAnimalSequence(jobKey: number, sortConfig: TableSortConfig[]): Promise<boolean> {
        const data = {JobKey: String(jobKey), TableSorts: sortConfig}
        return this.jobPharmaDataAccessService.checkAnimalSequence(data);
    }

    async updateAnimalSequence(jobKey: number, sortConfig: TableSortConfig[]): Promise<any> {
        const animalSequenceCheck = await this.checkAnimalSequence(jobKey, sortConfig);
        if (animalSequenceCheck) {
            await this.jobPharmaDataAccessService.updateAnimalSequence({JobKey: String(jobKey), TableSorts: sortConfig});
        }
        return animalSequenceCheck;
    }

    onPasteAnimals(job: Entity<Job & ExtendedJob> | Entity<Job>): Promise<any> {
        if (!this.copyBufferService.hasAnimals()) {
            return Promise.resolve();
        }
        const animals = this.copyBufferService.paste();
        return this.addMaterialsToJob(animals, job);
    }

    getTaskStatusCounts(jobKey: number): Promise<any> {
        return this.jobPharmaDataAccessService.getTaskStatusCounts(jobKey);
    }

    getProtocolStatusCounts(jobKey: number): Promise<any> {
        return this.jobPharmaDataAccessService.getProtocolStatusCounts(jobKey);
    }

    onSaveSuccessful() {
        this.pendingAnimalJobMaterialDeletions = new Map();
    }

    onDiscardChanges() {
        this.pendingAnimalJobMaterialDeletions = new Map();
    }
}
